// The Share tab: the link an anonymous visitor fills the workflow in through.
//
// One token per workflow, minted by the API and shown here as a URL on this
// same host under /fill/ — the fill app is a second bundle on the same
// distribution, so the origin the tenant is looking at is the origin the link
// names. Enable and Regenerate are the same mutation: `enabled: true` always
// mints a fresh token, and the old link stops resolving the moment the row is
// written. Disable removes it.
//
// WITH the trailing slash, deliberately. The CloudFront function on the
// default behaviour 301s any extension-less last segment to its slash form,
// and the token's alphabet is exactly what that rule takes for a segment —
// so the slash form is the one that answers in one round trip.
//
// Beside the link, the same form as a snippet for the tenant's own page: an
// <iframe> on the link with `?embed=1`, and the few lines of script that
// keep the frame as tall as the form inside it, which posts its height as
// `wfws:resize` (and `wfws:done` when a response lands — see the fill app's
// components/flow/embed). An iframe and not a script that renders into the
// host's page, because inside the frame everything the fill app relies on —
// its captcha on this origin, same-origin GraphQL, its own stylesheet — stays
// exactly as it is at the link; docs/wfws.md records the alternative and why
// not. `/fill/*` is served without X-Frame-Options so any site may frame it.
//
// A workflow with HIDDEN text fields gets them in the snippet's URL, empty —
// `?embed=1&source=` — because the page that embeds the form is what fills
// them in: one workflow on three sites is one list of responses, and each
// site writes its own name after `source=` to tell its responses apart. See
// the fill app's components/flow/prefill.
import React from "react";
import { Row, Col, Alert } from "reactstrap";
import { useMutation } from "@apollo/client";
import * as Notify from "@repoflow/form-schema/notify";
import { Icon as LoadingIcon } from "@repoflow/components/loading";
import { hiddenFields } from "@common/schema/types";
import * as WorkflowQL from "queries/workflow";

export const shareLink = (shareToken) =>
  `${window.location.origin}/fill/${shareToken}/`;

// What an attribute value may not carry. The title is the tenant's own text.
const attribute = (text) =>
  String(text).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

/**
 * The embed snippet: the frame, then the script that sizes it.
 *
 * The script matches the message's SOURCE to the frame and not only its type,
 * so a page with two forms on it — or any other frame posting a `wfws:resize`
 * of its own — sizes the right one. The starting height is what a one-step
 * form roughly needs, and the first `wfws:resize` replaces it the moment the
 * form has rendered. `fields` are the hidden ones the page fills in, each
 * left for the tenant to complete.
 */
export const embedSnippet = (shareToken, title = "", fields = []) =>
  [
    `<iframe id="wfws-fill" src="${shareLink(shareToken)}?embed=1${fields.map((name) => `&${encodeURIComponent(name)}=`).join("")}" title="${attribute(title || "Form")}" style="width:100%;border:0" height="480"></iframe>`,
    `<script>`,
    `(function () {`,
    `  var frame = document.getElementById("wfws-fill");`,
    `  window.addEventListener("message", function (event) {`,
    `    if (event.source !== frame.contentWindow || !event.data) return;`,
    `    if (event.data.type === "wfws:resize") frame.style.height = event.data.height + "px";`,
    `  });`,
    `})();`,
    `</script>`,
  ].join("\n");

export const ShareTab = ({ workflow }) => {
  const { workflowId, shareToken } = workflow;
  const publishedVersion = workflow.publishedVersion || 0;
  // What the link SERVES is the published version, so it is that version's
  // steps that say whether the link opens on a form or on "no steps yet".
  const steps = (workflow.published && workflow.published.steps) || [];
  const hidden = hiddenFields(steps);

  const [share, { loading }] = useMutation(WorkflowQL.share, {
    // The answer carries the workflow under the same cache id, which is
    // enough on its own; the refetch is what makes this screen's Section
    // query — the route's — agree with it without a navigation.
    refetchQueries: [{ query: WorkflowQL.get, variables: { workflowId } }],
    awaitRefetchQueries: true,
    onError: (error) => {
      console.error("workflow.share:", error);
      Notify.error("The share link could not be changed. Please try again.");
    },
  });

  const set = (enabled) => share({ variables: { workflowId, enabled } });

  const copy = (text, what) => async () => {
    try {
      await navigator.clipboard.writeText(text);
      Notify.success(`${what} copied.`);
    } catch (e) {
      // No clipboard permission, or no secure context. The text is in the
      // field beside the button, so nothing is lost.
      Notify.warning(`Could not copy — select the ${what.toLowerCase()} and copy it by hand.`);
    }
  };

  return (
    <div id="workflow-share" className="mt-3">
      {!publishedVersion ? (
        <Alert color="warning" id="share-unpublished">
          This workflow has not been published. The link opens nothing until
          it is — publish it above, then share it.
        </Alert>
      ) : steps.length === 0 ? (
        <Alert color="warning">
          The published version has no steps. The link will open, and say so,
          until some are added and published.
        </Alert>
      ) : workflow.changed ? (
        <Alert color="light" id="share-changed" className="text-muted">
          The link serves version {publishedVersion}. The draft has changes
          that visitors will not see until it is published.
        </Alert>
      ) : null}

      {!shareToken ? (
        <>
          <p className="text-muted">
            Not shared. Anyone with the link can fill this workflow in without
            signing in, and every completed response is listed under Responses.
            The link stays valid until it is regenerated or disabled here.
          </p>
          <button
            type="button"
            id="share-enable"
            className="btn btn-primary"
            disabled={loading}
            onClick={() => set(true)}
          >
            {loading ? <LoadingIcon /> : "Enable sharing"}
          </button>
        </>
      ) : (
        <>
          <p className="text-muted">
            Anyone with this link can fill the workflow in without signing in.
          </p>
          <Row className="align-items-center">
            <Col md={8}>
              <input
                id="share-link"
                className="form-control"
                readOnly
                value={shareLink(shareToken)}
                onFocus={(e) => e.target.select()}
              />
            </Col>
            <Col md={4} className="mt-2 mt-md-0">
              <button
                type="button"
                id="share-copy"
                className="btn btn-primary"
                onClick={copy(shareLink(shareToken), "Link")}
              >
                Copy
              </button>{" "}
              <button
                type="button"
                id="share-regenerate"
                className="btn btn-secondary"
                disabled={loading}
                onClick={() => set(true)}
              >
                {loading ? <LoadingIcon /> : "Regenerate"}
              </button>{" "}
              <button
                type="button"
                id="share-disable"
                className="btn btn-danger"
                disabled={loading}
                onClick={() => set(false)}
              >
                Disable
              </button>
            </Col>
          </Row>
          <p className="text-muted small mt-2">
            Regenerating replaces the link; the old one stops working at once.
          </p>

          <h6 className="mt-4">Embed in your own page</h6>
          <p className="text-muted">
            Paste this where the form should appear in any HTML page. The form
            runs inside the frame and the frame grows and shrinks with it. In
            a section of a site, put the <code>&lt;iframe&gt;</code> in the
            section&apos;s HTML and the script&apos;s body in its Script field,
            so the editor&apos;s preview sizes the frame as the site does.
            Regenerating the link breaks every page that embedded it, the same
            as the link.
          </p>
          {hidden.length > 0 ? (
            <p id="share-hidden" className="text-muted">
              This form has hidden fields —{" "}
              {hidden.map((name, index) => (
                <React.Fragment key={name}>
                  {index > 0 ? ", " : null}
                  <code>{name}</code>
                </React.Fragment>
              ))}{" "}
              — that the visitor never sees. Write each page&apos;s own value
              after its <code>=</code> in the frame&apos;s address, such as{" "}
              <code>&amp;{hidden[0]}=www.example.com</code>, and every response
              will say which page it came from. A value longer than the
              field allows, or one its pattern refuses, is ignored.
            </p>
          ) : null}
          <Row className="align-items-start">
            <Col md={8}>
              <textarea
                id="share-embed"
                className="form-control text-monospace small"
                readOnly
                rows={10}
                value={embedSnippet(shareToken, workflow.title, hidden)}
                onFocus={(e) => e.target.select()}
              />
            </Col>
            <Col md={4} className="mt-2 mt-md-0">
              <button
                type="button"
                id="share-embed-copy"
                className="btn btn-primary"
                onClick={copy(embedSnippet(shareToken, workflow.title, hidden), "Snippet")}
              >
                Copy
              </button>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};
